import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ImageCarousel = ({ 
  partnerMedia = [],
  autoPlay = false,
  interval = 5000
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Minimum swipe distance for gesture detection (in pixels)
  const minSwipeDistance = 50;

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1280) setItemsPerSlide(5);      // xl
      else if (width >= 1024) setItemsPerSlide(4); // lg
      else if (width >= 768) setItemsPerSlide(3);  // md
      else if (width >= 640) setItemsPerSlide(2);  // sm
      else setItemsPerSlide(1);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let timer;
    if (autoPlay && !isPaused) {
      timer = setInterval(() => {
        setCurrentIndex((prev) => 
          (prev + 1) % Math.ceil(partnerMedia.length / itemsPerSlide)
        );
      }, interval);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [autoPlay, interval, partnerMedia.length, itemsPerSlide, isPaused]);

  const goToPrevious = () => {
    setIsPaused(true);
    setCurrentIndex((prev) => 
      (prev - 1 + Math.ceil(partnerMedia.length / itemsPerSlide)) % 
      Math.ceil(partnerMedia.length / itemsPerSlide)
    );
    setTimeout(() => setIsPaused(false), interval);
  };

  const goToNext = () => {
    setIsPaused(true);
    setCurrentIndex((prev) => 
      (prev + 1) % Math.ceil(partnerMedia.length / itemsPerSlide)
    );
    setTimeout(() => setIsPaused(false), interval);
  };

  const goToSlide = (index) => {
    setIsPaused(true);
    setCurrentIndex(index);
    setTimeout(() => setIsPaused(false), interval);
  };

  const getImageUrl = (imageUrl) => {
    const baseUrl = process.env.REACT_APP_API_URL ;
    const mediaUrl = `${baseUrl}${imageUrl}`;
    return mediaUrl;
  };

  // Touch event handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  return (
    <div className="carousel">
      <div className="carousel__container">
        <div 
          className="carousel__track"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {[...Array(Math.ceil(partnerMedia.length / itemsPerSlide))].map((_, slideIndex) => (
            <div key={slideIndex} className="carousel__slide">
              {partnerMedia
                .slice(
                  slideIndex * itemsPerSlide,
                  (slideIndex + 1) * itemsPerSlide
                )
                .map((image) => (
                  <div key={image.id} className="carousel__item">
                    <img
                      src={getImageUrl(image.url)}
                      alt={image.alternativeText || image.name}
                      draggable={false} // Prevent image dragging during swipe
                    />
                  </div>
                ))}
            </div>
          ))}
        </div>
        
        <button
          onClick={goToPrevious}
          className="carousel__button carousel__button--prev"
          aria-label="Previous slide"
        >
          <ChevronLeft />
        </button>

        <button
          onClick={goToNext}
          className="carousel__button carousel__button--next"
          aria-label="Next slide"
        >
          <ChevronRight />
        </button>

        <div className="carousel__dots">
          {[...Array(Math.ceil(partnerMedia.length / itemsPerSlide))].map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`carousel__dot ${index === currentIndex ? 'carousel__dot--active' : ''}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImageCarousel;